import { z } from 'zod';
import { IAddress } from './address';
import { IAsset } from './asset';
import { CurrencyCode } from './base';
import { SalesChannelType } from './saleschannel';
import { ISKU } from './skus';

export enum CourierPartner {
  SELF = 'SELF',
  SHIPROCKET = 'SHIPROCKET',
  SHIPROCKET_QUICK = 'SHIPROCKET_QUICK',
  SHIPROCKET_AIR = 'SHIPROCKET_AIR',
  PORTER = 'PORTER',
  SHIPROCKET_STANDARD = 'SHIPROCKET_STANDARD',
  SELF_EXPRESS = 'SELF_EXPRESS',
  SELF_PRIORITY = 'SELF_PRIORITY',
  INTERNATIONAL = 'INTERNATIONAL',
  LYSKRAFT = 'LYSKRAFT',
  DTDC = 'DTDC',
  BLUEDART = 'BLUEDART',
  BLUEDART_BLR = 'BLUEDART_BLR',
  BLUEDART_MUM = 'BLUEDART_MUM',
  BLUEDART_SURFACE = 'BLUEDART_SURFACE',
  BLUEDART_PRIORITY = 'BLUEDART_PRIORITY',
  BLUEDART_TDD = 'BLUEDART_TDD',
  ESHIPZ = 'ESHIPZ',
  SHADOWFAX = 'SHADOWFAX',
  TRACKON = 'TRACKON',
  MERCHANTS_COURIER = 'MERCHANTS_COURIER',
  SHREE_MARUTI = 'SHREE_MARUTI',
}

export type OrderLineActionCode =
  | 'alteration_complete'
  | 'qc_fail_reship'
  | 'customization_review_done'
  | 'brand_accepted'
  | 'brand_declined'
  | 'brand_status_updated'
  | 'brand_dispatched'
  | 'b2b_po_received_at_warehouse'
  | 'cancel'
  | 'b2b2c_brand_cancel'
  | 'return'
  | 'exchange';

export enum OrderLineOnHoldReason {
  NA = 'NA',
  PENDING_ALTERATION = 'PENDING_ALTERATION',
  COD_CONFIRMATION_PENDING = 'COD_CONFIRMATION_PENDING',
  RETURN_PENDING = 'RETURN_PENDING',
  FREQUENT_RETURNER = 'FREQUENT_RETURNER',
  TRY_AT_HOME_CONSOLIDATION = 'TRY_AT_HOME_CONSOLIDATION',
}

export enum RefundMode {
  STORE_CREDIT = 'STORE_CREDIT',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  CASH = 'CASH',
  UPI = 'UPI',
  SOURCE = 'SOURCE',
  NONE = 'NONE',
  OTHERS = 'OTHERS',
}

export enum PackageStatus {
  CREATED = 'CREATED',
  FULFILLABLE = 'FULFILLABLE',
  WIP = 'WIP',
  PACKED = 'PACKED',
  DISPATCHED = 'DISPATCHED',
  DELIVERED_AT_STORE = 'DELIVERED_AT_STORE',
  RECEIVED_AT_STORE = 'RECEIVED_AT_STORE',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  CANCELLED = 'CANCELLED',
  RTO_INITIATED = 'RTO_INITIATED',
  RTO = 'RTO',
  DELIVERED = 'DELIVERED',
}

export enum RefundRequestStatus {
  CREATED = 'CREATED',
  INITIATED = 'INITIATED',
  PROCESSED = 'PROCESSED',
  FAILED = 'FAILED',
  DISCARDED = 'DISCARDED',
}

export enum RefundTriggerAction {
  CANCEL = 'CANCEL',
  RETURN = 'RETURN',
  RTO = 'RTO',
  EXCHANGE = 'EXCHANGE',
  EXPRESS_SHIPPING_DELAY = 'EXPRESS_SHIPPING_DELAY',
}

export enum PaymentStatus {
  PAID = 'PAID',
  PENDING = 'PENDING',
}

export enum PaymentMethod {
  COD = 'COD',
  PREPAID = 'PREPAID',
}

export enum RiderCashHandoverStatus {
  PENDING_RIDER_CONFIRMATION = 'PENDING_RIDER_CONFIRMATION',
  PENDING_HUB_CONFIRMATION = 'PENDING_HUB_CONFIRMATION',
  COMPLETED = 'COMPLETED',
  DISPUTED = 'DISPUTED',
  CANCELLED = 'CANCELLED',
}

export enum RiderCashHandoverItemStatus {
  PENDING = 'PENDING',
  RIDER_CONFIRMED = 'RIDER_CONFIRMED',
  COMPLETED = 'COMPLETED',
  DISPUTED = 'DISPUTED',
  CANCELLED = 'CANCELLED',
}

export enum RiderCashHandoverProvider {
  LYSKRAFT_RIDER = 'LYSKRAFT_RIDER',
  SHADOWFAX = 'SHADOWFAX',
}

export enum RiderTrackingStatus {
  TRACKING_OK = 'TRACKING_OK',
  STALE_PING = 'STALE_PING',
  PERMISSION_REVOKED = 'PERMISSION_REVOKED',
  BATTERY_OPTIMIZED_OR_KILLED = 'BATTERY_OPTIMIZED_OR_KILLED',
  APP_FORCE_STOPPED_OR_NO_RECOVERY_PING = 'APP_FORCE_STOPPED_OR_NO_RECOVERY_PING',
  DEVICE_CHANGED = 'DEVICE_CHANGED',
  OFFLINE_BUFFERING = 'OFFLINE_BUFFERING',
  NOTIFICATION_DEGRADED = 'NOTIFICATION_DEGRADED',
  UNKNOWN = 'UNKNOWN',
}

export interface IRiderLatestState {
  id: number;
  riderId: number;
  deviceId?: string;
  app?: string;
  os?: string;
  appVersion?: string;
  sessionId?: string;
  trackingStatus: RiderTrackingStatus;
  lastPingAt: string;
  lastEventAt?: string | null;
  lastHealthAt?: string | null;
  lastLocationAt?: string | null;
  lastFacilityArrivalAt?: string | null;
  lastFacilityArrivalFacilityId?: number | null;
  lastFacilityArrivalDistanceKm?: number | null;
  lastFacilityArrivalStatus?: string | null;
  lastReceivedAt: string;
  lastEventId: string;
  lastEventName: string;
  eventSequence?: number;
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  batteryPercent?: number;
  charging?: boolean;
  foregroundServiceRunning?: boolean;
  locationPermission?: string;
  notificationPermission?: string;
  batteryOptimizationIgnored?: boolean;
  activeShiftId?: number;
  activePackageId?: number;
  activeReturnId?: number;
  metadata?: Record<string, unknown>;
}

export interface IRiderControlCentreWork {
  workType: 'PACKAGE' | 'RETURN' | 'TRY_AT_HOME' | string;
  workId: number;
  orderId?: number;
  salesChannelOrderId?: string;
  status?: string;
  deliveryMode?: string;
  fulfillmentMode?: string;
  tryAtHome?: boolean;
  tryAtHomeSlot?: string;
  express?: boolean;
  priority?: boolean;
  facilityId?: number;
  receivingFacilityId?: number;
  expectedBy?: string;
  promisedDeliveryAt?: string;
  estimatedDeliveryAt?: string;
  statusSince?: string;
  createdOn?: string;
  targetType?: string;
  inFlight?: boolean;
}

export interface IRiderControlCentreRider {
  riderId: number;
  operatingFacility?: {
    id?: number;
    name?: string;
    source?: string;
    coordinatesAvailable?: boolean;
  };
  shift?: {
    id: number;
    status: string;
    facilityId?: number;
    startedAt?: string;
    endedAt?: string;
  };
  attendance?: {
    status: 'ON_SHIFT' | 'SHIFT_COMPLETED' | 'NOT_STARTED' | 'UNAVAILABLE' | string;
    statusLabel: string;
    firstShiftStartedAt?: string;
    activeShiftStartedAt?: string;
    lastShiftEndedAt?: string;
    shiftCount?: number;
  };
  operationalState: string;
  operationalStateLabel: string;
  operationalBucket?:
    | 'AVAILABLE'
    | 'ON_JOB'
    | 'AWAY'
    | 'ASSIGNED'
    | 'OFF_SHIFT'
    | 'UNAVAILABLE'
    | string;
  operationalBucketLabel?: string;
  latestState?: IRiderControlCentreDeviceState;
  workload?: {
    forward: number;
    tryAtHome: number;
    returns: number;
    total: number;
    inFlight: number;
    overdue?: number;
    staleAssignments?: number;
    oldestExpectedBy?: string;
  };
  currentWork?: IRiderControlCentreWork;
  priorityWork?: IRiderControlCentreWork;
  reportedActiveWork?: IRiderControlCentreWork;
  reportedActiveWorkMatchStatus?:
    | 'MATCHED'
    | 'NOT_REPORTED'
    | 'STALE_REFERENCE'
    | 'AMBIGUOUS'
    | 'NO_IN_FLIGHT_WORK'
    | 'UNAVAILABLE'
    | string;
  expectation?: {
    expectedBy?: string;
    source?: string;
    overdue?: boolean;
  };
  movement?: {
    targetType?: string;
    haversineDistanceKm?: number;
    facilityDistanceKm?: number;
    workTargetDistanceKm?: number;
    workTargetType?: string;
    workType?: string;
    workId?: number;
    atFacility?: boolean;
    evidence?: string;
    lastLocationAt?: string;
    locationAccuracyMeters?: number;
  };
  cash?: {
    unlinkedAmount: number;
    activeHandoverAmount: number;
    disputedAmount: number;
    totalAmount: number;
    openHandoverCount: number;
    hasDispute: boolean;
    state?: 'NONE' | 'WITH_RIDER' | 'HANDOVER_PENDING' | 'DISPUTED' | string;
    stateLabel?: string;
  };
  workIssues?: Array<IRiderControlCentreIssue>;
  trackingIssues?: Array<IRiderControlCentreIssue>;
  setupIssues?: Array<IRiderControlCentreIssue>;
  needsAttention: boolean;
  attentionReasons: Array<string>;
}

export interface IRiderControlCentreIssue {
  code: string;
  label: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL' | string;
  detail?: string;
}

export interface IRiderControlCentreDeviceState {
  appVersion?: string;
  trackingStatus?: RiderTrackingStatus;
  lastPingAt?: string;
  lastEventAt?: string | null;
  lastHealthAt?: string | null;
  lastLocationAt?: string | null;
  lastFacilityArrivalAt?: string | null;
  lastFacilityArrivalFacilityId?: number | null;
  lastFacilityArrivalDistanceKm?: number | null;
  lastFacilityArrivalStatus?: string | null;
  lastReceivedAt?: string;
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  batteryPercent?: number;
  charging?: boolean;
  foregroundServiceRunning?: boolean;
  locationPermission?: string;
  notificationPermission?: string;
  batteryOptimizationIgnored?: boolean;
  activeShiftId?: number;
  activePackageId?: number;
  activeReturnId?: number;
}

export interface IRiderControlCentreResponse {
  generatedAt: string;
  refreshAfterSeconds: number;
  summary: {
    riders: number;
    onShift: number;
    attendedToday: number;
    completedShift: number;
    notStarted: number;
    available?: number;
    onJob?: number;
    away?: number;
    assigned?: number;
    offShift?: number;
    stateUnavailable?: number;
    atFacility: number;
    inField: number;
    expectedBack: number;
    needsAttention: number;
    ridersWithWorkIssues?: number;
    ridersWithTrackingIssues?: number;
    ridersWithSetupIssues?: number;
    ridersWithCashDisputes?: number;
    ridersWithPendingCashHandover?: number;
    ridersWithCash: number;
    cashWithRiders: number;
  };
  riders: Array<IRiderControlCentreRider>;
  sectionStatus: Record<string, { status: 'AVAILABLE' | 'PARTIAL' | 'UNAVAILABLE'; message?: string }>;
}

export interface IRiderControlCentreEtaResponse {
  generatedAt: string;
  items: Array<{
    riderId: number;
    workType?: string;
    workId?: number;
    facilityId?: number;
    targetType?: string;
    status: 'AVAILABLE' | 'PARTIAL' | 'UNAVAILABLE';
    source?: string;
    haversineDistanceKm?: number;
    roadDistanceKm?: number;
    durationSeconds?: number;
    estimatedArrivalAt?: string;
    calculatedAt: string;
    message?: string;
  }>;
}

export interface IRiderControlCentreNavigationResponse {
  riderId: number;
  status: 'AVAILABLE' | 'UNAVAILABLE';
  targetType?: string;
  targetLabel?: string;
  facilityId?: number;
  work?: IRiderControlCentreWork;
  sourceLocationAt?: string;
  sourceLocationFreshness: 'FRESH' | 'STALE' | 'UNKNOWN';
  navigationUrl?: string;
  message?: string;
}

export interface IRiderEventHistory {
  id: number;
  eventId: string;
  eventName: string;
  riderId?: number;
  deviceId?: string;
  eventTs: string;
  receivedAt: string;
  processedAt: string;
  status: string;
  errorMessage?: string;
}

export interface IRiderCashHandoverItem {
  id: number;
  createdBy?: string;
  createdOn?: string;
  lastModifiedOn?: string;
  version?: number;
  handoverId: number;
  packageId: number;
  orderId: number;
  salesChannelOrderId: string;
  riderIdAtCapture?: number;
  facilityId?: number;
  collectionPartyType?: RiderCashHandoverProvider;
  courierPartner?: string;
  courierTrackingId?: string;
  externalRiderId?: string;
  externalRiderName?: string;
  externalRiderContact?: string;
  collectedAmount: number;
  packageDeliveredOn?: string;
  status: RiderCashHandoverItemStatus;
  metadata?: Record<string, unknown>;
}

export interface IRiderCashHandover {
  id: number;
  createdBy?: string;
  createdOn?: string;
  lastModifiedOn?: string;
  version?: number;
  riderId?: number;
  facilityId?: number;
  receivingFacilityId?: number;
  collectionPartyType?: RiderCashHandoverProvider;
  courierPartner?: string;
  courierTrackingId?: string;
  externalRiderId?: string;
  externalRiderName?: string;
  externalRiderContact?: string;
  status: RiderCashHandoverStatus;
  expectedAmount: number;
  riderConfirmedAmount?: number;
  hubCountedAmount?: number;
  varianceAmount?: number;
  otpAttempts?: number;
  riderConfirmedBy?: number;
  hubConfirmedBy?: number;
  disputeResolvedBy?: number;
  cancelledBy?: number;
  riderConfirmedOn?: string;
  hubConfirmedOn?: string;
  disputeResolvedOn?: string;
  completedOn?: string;
  cancelledOn?: string;
  disputeResolvedAmount?: number;
  remarks?: string;
  disputeReason?: string;
  metadata?: Record<string, unknown>;
  items?: Array<IRiderCashHandoverItem>;
  otp?: string;
}

export interface IRiderCashHandoverCandidate {
  packageId: number;
  orderId: number;
  salesChannelOrderId: string;
  riderId?: number;
  facilityId?: number;
  collectionPartyType?: RiderCashHandoverProvider;
  collectedAmount: number;
  deliveredOn?: string;
  tryAtHome?: boolean;
  paymentMethod?: string;
  courierPartner?: string;
  courierTrackingId?: string;
  externalRiderId?: string;
  externalRiderName?: string;
  externalRiderContact?: string;
  customerName?: string;
  customerPhone?: string;
}

export enum OrderLineBrandStatus {
  // ADDED = 'ADDED',
  INTERNAL_REVIEW_DONE = 'INTERNAL_REVIEW_DONE',
  SHARED = 'SHARED',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  DISPATCHED = 'DISPATCHED',
  RECEIVED_AT_WAREHOUSE = 'RECEIVED_AT_WAREHOUSE',
  QC_FAILED = 'QC_FAILED',
  QC_PASSED = 'QC_PASSED',
}

export enum OrderLineStatus {
  CREATED = 'CREATED',
  BRAND_WIP = 'BRAND_WIP',
  FULFILLABLE = 'FULFILLABLE',
  UNFULFILLABLE = 'UNFULFILLABLE',
  WIP = 'WIP', // pushed into unicom/warehouse (after brand status = qc_pass in case of MTO+jit+mtc)
  PACKED = 'PACKED',
  DISPATCHED = 'DISPATCHED',

  READY_FOR_PICKUP = 'READY_FOR_PICKUP',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',

  RTO_INITIATED = 'RTO_INITIATED',
  RTO = 'RTO',

  RETURN_WIP = 'RETURN_WIP',
  RETURNED = 'RETURNED',
}

export enum DeliveryMode {
  STORE_PURCHASE = 'STORE_PURCHASE',
  STORE_PICKUP = 'STORE_PICKUP',
  HOME_DELIVERY = 'HOME_DELIVERY',
}

export enum FulfillmentMode {
  ONHAND = 'ONHAND',
  JIT = 'JIT', // MTO with inventory visibility
  MTO = 'MTO',
  MTC = 'MTC',
}

export enum VendorProgressStatus {
  PENDING_UPDATE = 'PENDING_UPDATE',
  UPDATED_ON_TRACK = 'UPDATED_ON_TRACK',
  UPDATED_WITH_REVISED_DATE = 'UPDATED_WITH_REVISED_DATE',
}

export const OrderSourceChannelSchema = z.enum(['IOS', 'ANDROID', 'WEB', 'MWEB']);

export type OrderSourceChannel = z.infer<typeof OrderSourceChannelSchema>;

export interface IIncentiveCredit {
  value?: number;
  status?: string;
  firstOrderCreditNoteId?: number;
  issuedAt?: string;
  expiresAt?: string;
  appInstallClaimExpiresAt?: string;
  appInstallVerifiedAt?: string;
  revokedAt?: string;
  revokedReason?: string;
  policyCode?: string;
  activationGate?: string;
  rate?: number;
  cap?: number;
  entitlementLineageVersion?: number;
  entitlementRootOrderId?: number;
  entitlementRootSalesChannelOrderId?: string;
  entitlementRootAmount?: number;
  entitlementSegmentRootLineItemIds?: number[];
  entitlementSegmentAmount?: number;
  entitlementSegmentKey?: string;
}

export interface ICustomerOrder {
  id: number;

  billTime: string;
  createdOn: string;
  paymentOrderId: number;
  cartId: number;

  salesChannelId: number;
  salesChannelType: SalesChannelType;
  salesFacilityId: number;
  salesFacilityGroupId: number;
  salesChannelTerminalId: number;
  salesChannelOrderId: string;

  customerId?: number;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerGSTIN?: string;
  source?: string;
  sourceChannel?: OrderSourceChannel;
  customerBillingAddressId?: number;
  customerBillingAddress?: IAddress;

  totalQuantity: number;
  totalNet: number;

  // sale staff details
  salesStaffId: number;

  salesStylistId?: number;
  // bill
  billAssetId: number;

  currency: CurrencyCode;

  // items
  lineItems: Array<IOrderLine>;

  // shipments
  packages: Array<IOrderPackage>;

  // charges
  costBreakup: ICostBreakup;

  isExchange: boolean;

  couponCode?: string;
  couponApplicationRemarks?: string;

  firstOrderIncentiveCredit?: IIncentiveCredit;
  firstOrderCreditIncentiveActivateOn?: string;
  firstOrderIncentiveEffectiveAppInstallClaimExpiresAt?: string;
  firstOrderIncentiveQualifyingAppInstallAt?: string;
  firstOrderIncentiveEffectiveLifecycle?: IFirstOrderIncentiveEffectiveLifecycle;
}

export interface IFirstOrderIncentiveEffectiveLifecycle {
  state?: 'RTO_IN_PROGRESS' | 'NOT_AVAILABLE';
  reason?: 'RTO_INITIATED' | 'RTO' | 'RETURNED' | 'CANCELLED';
}

export interface IShelfSuggestionOption {
  shelf?: string;
  section?: string;
  inventoryType?: string;
  priority?: number;
  quantity?: number;
  quantityBlocked?: number;
  quantityNotFound?: number;
  quantityDamaged?: number;
  availableQty?: number;
}

export interface IShelfSuggestion {
  status?: string;
  reason?: string;
  primaryShelf?: string;
  facilityId?: number;
  unicomFacilityCode?: string;
  unicomFacilityName?: string;
  suggestions?: Array<IShelfSuggestionOption>;
  sourceSyncedAt?: string;
  suggestedAt?: string;
}

export interface IOrderLine {
  id: number;
  createdOn: string;
  unicomSaleOrderItemCode?: string;
  packageId: number;
  orderId: number;

  salesStaffId: number;
  salesStylistId?: number;
  salesChannelId: number;
  salesChannelType: SalesChannelType;
  salesChannelOrderId: string;
  customerId?: number;
  customerName?: string;
  customerPhone?: string;

  fulfillmentMode: FulfillmentMode;
  fulfillmentFacilityId: number;
  fulfillmentFacilityGroupId: number;
  isSameStorePurchase: boolean;
  fulfillmentFacilityType: string;
  fulfillmentFacilityAddress: IAddress;
  poReceiveFacilityId?: number;

  skuId: number;
  skuCode: string;
  vendorSkuCode?: string;
  skuBarcode?: string;
  shelfSuggestion?: IShelfSuggestion;
  productName: string;
  productImage?: IAsset;
  color: string;
  size: string;
  hsnCode: string;
  brandId: number;
  vendorId: number;
  brandName: string;
  tryAtHome?: boolean;

  status: OrderLineStatus;
  brandStatus?: OrderLineBrandStatus;
  brandJitFulfillmentModel?: string;

  alterationEntry?: IAlterationDetails;

  customerDeliveryAddressId?: number;
  customerDeliveryAddress?: IAddress;
  customerDeliveryStoreId?: number;
  customerDeliveryStoreAddress?: IAddress;
  deliveryMode: DeliveryMode;
  isExpress: boolean;
  isPriority: boolean;
  opsTat: number;
  vendorDeliveryDate?: string;
  dispatchByDate?: string;
  warehouseDeliveryByDate?: string;
  vendorToWarehouseTatDays?: number;
  mtoVendorEffectiveDueDate?: string;
  vendorDeliveryDateHistory?: Array<IVendorDeliveryDateRevision>;
  effectiveBrandDispatchedOn?: string;
  isEffectivelyBrandDispatched?: boolean;
  acceptedOn?: string;
  lastVendorProgressUpdateOn?: string;
  vendorProgressStatus?: VendorProgressStatus;
  vendorProgressDisplayText?: string;
  mtoVendorProspectivePenalty?: {
    operationalEffectiveDueDate?: string;
    fullTatDueDate?: string;
    effectiveDueDate?: string;
    penaltyPolicyVersion?: string;
    penaltyBaseTatDays?: number;
    penaltyBaseTatSource?: string;
    operationalEffectiveDueGraceDays?: number;
    fullTatDueGraceDays?: number;
    penaltyGraceDays?: number;
    penaltyGraceCutoffDate?: string;
    effectiveLagDays?: number;
    graceLagDays?: number;
    prospectivePenaltyRate?: number | string;
    prospectivePenaltyAmount?: number | string;
    prospectivePenaltyCategory?: string;
  };
  mtoVendorPenalty?: {
    status?: string;
    penaltyRate?: number | string;
    penaltyAmount?: number | string;
    penaltyCategory?: string;
    effectiveLagDays?: number;
    graceLagDays?: number;
    operationalEffectiveDueDate?: string;
    fullTatDueDate?: string;
    appliedOn?: string;
    effectiveDueDate?: string;
    penaltyPolicyVersion?: string;
    penaltyBaseTatDays?: number;
    penaltyBaseTatSource?: string;
    operationalEffectiveDueGraceDays?: number;
    fullTatDueGraceDays?: number;
    penaltyGraceDays?: number;
    penaltyGraceCutoffDate?: string;
    correctionOn?: string;
    correctionReference?: string;
    correctionReason?: string;
    previousPenaltyStatus?: string;
    previousPenaltyRate?: number | string;
    previousPenaltyAmount?: number | string;
    previousEffectiveDueDate?: string;
    previousPenaltyPolicyVersion?: string;
    penaltyPoCode?: string;
    supersededByPoCode?: string;
    supersededReason?: string;
  };
  courierTat: number;
  promisedDeliveryDate: string;
  estimatedDeliveryDate: string;
  actualDeliveryDate?: string;

  returnable: boolean;
  exchangeable: boolean;
  currentlyReturnable?: boolean;
  currentlyExchangeable?: boolean;
  currentlyReturnableByAdmin?: boolean;
  currentlyExchangeableByAdmin?: boolean;
  returnableUntil?: string;
  exchangeableUntil?: string;
  cancellable?: boolean;
  cancellableByAdmin?: boolean;
  isAlterationAvailable?: boolean;

  costBreakup: ICostBreakup;
  paymentMethod: PaymentMethod;
  saleDiscountApplied?: boolean;
  history: Array<IOrderLineTrackingDetails>;

  onhold: boolean;
  onholdReason: OrderLineOnHoldReason;

  cancellationReason: string;

  returnRequestId?: number;
  returnReason?: string;
  returnSubReason?: string;
  returnEntry?: IReturnRequest;
  returnApprovalStatus?: 'APPROVAL_REQUESTED' | 'APPROVED' | 'DENIED' | null;
  returnRequestReason?: string | null;
  returnRequestApprovalSalesChannelId?: number | null;
  returnApprovalRequestedOn?: string | null;
  returnApprovalPolicy?: {
    eligible?: boolean | null;
    required?: boolean | null;
    reasons?: string[] | null;
    warning?: string | null;
  } | null;

  refundRequestId?: number;
  refunded?: boolean;
  refundRequest?: IRefundRequest;
  reshipRequest?: IReshipRequest;

  unicomPurchaseOrderCode: string;
  purchaseOrderCreatedOn: string;
  purchaseOrderPdfAsset: IAsset;

  productId: number;
  skuEntry?: ISKU;

  exchangedForLineItemId?: number;
  isExchange: boolean;
  giftVoucherCode?: string;

  giftVoucherAsset?: {
    pngAsset?: {
      createdBy: string;
      createdOn: string;
      lastModifiedOn: string;
      version: number;
      id: number;
      type: string;
      bucket: string;
      path: string;
      usage: string;
      source: string;
      url: string;
      cleanUrl: string;
      uploaded: boolean;
      ready: boolean;
    };
  };

  cancellationSalesStaffId?: number;
  taggedForFrequentReturn?: boolean;
  refundPolicyMetadata?: Record<string, unknown>;
  mtocMetadata?: Record<string, unknown>;
  firstOrderIncentiveCredit?: IIncentiveCredit;
}

export interface IOrderLineTrackingDetails {
  brandStatus: OrderLineBrandStatus;
  remark: string;
  eventDate: string; // We'll use string for ZonedDateTime
  icon: string;
  status?: OrderLineStatus;
  actorId?: number;
}

export interface IVendorDeliveryDateRevision {
  previousVendorDeliveryDate?: string;
  revisedVendorDeliveryDate: string;
  revisedAt: string;
  reason?: string;
  source:
    | 'INITIAL_STAMP'
    | 'BRAND_ACCEPTED'
    | 'BRAND_STATUS_UPDATE'
    | 'ADMIN_MANUAL_UPDATE'
    | 'PACKAGE_EDIT'
    | 'SYSTEM_RECOMPUTE'
    | 'AUDIT_BACKFILL';
  actorId?: number;
}

export interface IAlterationDetails {
  referenceColor: string;
  referenceSize: string;
  remarks: string;
  customizationCategory: CustomizationCategory;
  topwearAlterationEntry?: ITopwearAlterationDetails;
  bottomwearAlterationEntry?: IBottomwearAlterationDetails;
}

export interface ITopwearAlterationDetails {
  shoulder: string;
  bust: string;
  armHole: string;
  waist: string;
  hips: string;
  sleeveLength: string;
  sleeveRound: string;
  dressLength: string;
}

export interface IBottomwearAlterationDetails {
  waist: string;
  hips: string;
  thigh: string;
  pantLength: string;
}

export enum CustomizationCategory {
  DRESS = 'DRESS',
  TOP_WEAR = 'TOP_WEAR',
  BOTTOM_WEAR = 'BOTTOM_WEAR',
  CO_ORD_SET = 'CO_ORD_SET',
}

export interface IOfferDiscountEntry {
  bundleId: number;
  name: string;
}

export interface ICostBreakup {
  mrp: number;
  tradeDiscount: number;
  sellingPrice: number;
  taxRate: number;
  cgst: number;
  sgst: number;
  igst: number;
  taxValue: number;
  taxableValue: number;
  couponCode?: string;
  couponDiscount?: number;
  bagDiscount?: number;
  offerDiscount?: number;
  offerDiscountEntry?: IOfferDiscountEntry;
  shippingCharge?: number;
  tahServiceFeeAmount?: number;
  codCharge?: number;
  customCharges?: Array<ICustomCharge>;
  customChargesViaOrder?: Array<ICustomCharge>;
  internationalDuty: number;
  internationalTax: number;
  chargeableAmount: number;

  amountPaid: number;
  amountRemaining: number;
  amountPaidBeforeDelivery: number;
  amountPaidOnDelivery: number;

  refundAmount: number;

  currency?: CurrencyCode;
  amountPaidUsingCredits?: number;
}

export interface IRefundRequest {
  id?: number;
  action?: RefundTriggerAction | string;
  orderId?: number;
  salesChannelOrderId?: string;
  refundMode: RefundMode;
  refundRemarks?: string;
  refundAmount: number;
  originalRefundAmount?: number;
  refundAmountAdjustmentDone?: boolean;
  refundAmountAdjustmentReason?: string;
  refundAmountAdjustedOn?: string;
  upiHandle?: string;
  storeCreditNumber?: string;
  bankAccount?: IBankAccount;
  status: RefundRequestStatus;
  payoutTxnId?: string;
  payoutMetadata?: Record<string, string | null | undefined>;
  refundDisplay?: IRefundDisplay;
  onhold: boolean;
  onholdReason: string;
  onholdShelved?: boolean;
  onholdShelfNote?: string;
  onholdShelvedOn?: string;
  orderLineItemIds: Array<number>;
  createdOn?: string;
  createdBy?: string;
  lastModifiedOn?: string;
  lastModifiedBy?: string;
  customerId?: number;
  customerName?: string;
  customerPhone?: string;
  isTestOrder?: boolean;
  returnId?: number;
}

export interface IRefundDisplay {
  split?: boolean;
  title?: string;
  description?: string;
  totalAmount?: number;
  components?: IRefundDisplayComponent[];
  retainedReasons?: string[];
  adjustments?: IRefundAdjustment[];
}

export interface IRefundDisplayComponent {
  refundMode?: RefundMode;
  label?: string;
  amount?: number;
  status?: string;
  reference?: string;
}

export interface IRefundOptionQuote {
  eligible?: boolean;
  ineligibleReason?: string;
  action?: RefundTriggerAction | string;
  orderId?: number;
  salesChannelOrderId?: string;
  orderLineItemIds?: number[];
  refundAmount?: number;
  cashEquivalentRefundAmount?: number;
  sourceRefundAmount?: number;
  retainedStoreCreditAmount?: number;
  defaultRefundMode?: RefundMode;
  options?: IRefundOption[];
  warnings?: string[];
  adjustments?: IRefundAdjustment[];
}

export interface IRefundAdjustment {
  code?: string;
  type?: 'DEDUCTION' | string;
  label?: string;
  amount?: number;
  message?: string;
}

export interface IRefundOption {
  refundMode: RefundMode;
  label?: string;
  description?: string;
  enabled?: boolean;
  disabledReason?: string;
  primaryAmount?: number;
  retainedStoreCreditAmount?: number;
  split?: boolean;
  requiresBankAccount?: boolean;
  requiresUpiHandle?: boolean;
  components?: IRefundOptionComponent[];
  retainedReasons?: string[];
}

export interface IRefundOptionComponent {
  refundMode?: RefundMode;
  label?: string;
  amount?: number;
}

export interface IFrequentReturnRefundRelaxationQuoteLine {
  orderLineItemId: number;
  packageId?: number;
  skuCode?: string;
  productName?: string;
  taggedForFrequentReturn?: boolean;
  relaxed?: boolean;
  relaxedBy?: number;
  relaxedOn?: string;
  relaxationReason?: string;
  refundRequestId?: number;
  tahServiceFeeAmount?: number;
  action?: string;
}

export interface IFrequentReturnRefundRelaxationQuoteRefund {
  refundRequestId: number;
  status?: RefundRequestStatus;
  refundMode?: RefundMode;
  currentRefundAmount?: number;
  originalRefundAmount?: number;
  pendingAmount?: number;
  refundAmountAfterException?: number;
  action?: string;
  blockedReason?: string;
  alreadyRelaxed?: boolean;
  topUpAlreadyRequested?: boolean;
  topUpPayoutId?: string;
  topUpStatus?: string;
  orderLineItemIds?: Array<number>;
}

export interface IFrequentReturnRefundRelaxationQuote {
  orderId?: number;
  refundRequestId?: number;
  salesChannelOrderId?: string;
  eligible?: boolean;
  ineligibleReason?: string;
  totalPendingAmount?: number;
  lineItems?: Array<IFrequentReturnRefundRelaxationQuoteLine>;
  refunds?: Array<IFrequentReturnRefundRelaxationQuoteRefund>;
}

export interface IFrequentReturnRefundRelaxationResultOutcome {
  refundRequestId?: number;
  refundMode?: RefundMode;
  amount?: number;
  outcome?: string;
  requiresRetry?: boolean;
  payoutId?: string;
  payoutStatus?: string;
  storeCreditNumber?: string;
}

export interface IFrequentReturnRefundRelaxationResult {
  orderId?: number;
  refundRequestId?: number;
  totalPendingAmount?: number;
  relaxedOrderLineItemIds?: Array<number>;
  refundOutcomes?: Array<IFrequentReturnRefundRelaxationResultOutcome>;
}

export interface IBankAccount {
  accountNumber: string;
  accountName: string;
  ifsc: string;
  bankName: string;
}

export interface IDelayedExpressShippingRefundQuoteLine {
  orderLineItemId: number;
  packageId?: number;
  skuCode?: string;
  productName?: string;
  promisedDeliveryDate?: string;
  actualDeliveryDate?: string;
  shippingCharge: number;
  alreadyCompensatedShippingCharge: number;
  refundableShippingCharge: number;
  existingAdjustmentShippingCharge?: number;
  remainingRefundableShippingCharge?: number;
}

export interface IDelayedExpressShippingRefundQuote {
  orderId: number;
  salesChannelOrderId?: string;
  totalDelayedShippingCharge: number;
  alreadyCompensatedShippingCharge: number;
  existingAdjustmentAmount: number;
  refundableAmount: number;
  existingRefundRequestId?: number;
  existingRefundStatus?: RefundRequestStatus;
  sourceRefundEligible?: boolean;
  sourceRefundIneligibilityReason?: string;
  lineItems: Array<IDelayedExpressShippingRefundQuoteLine>;
}

export enum ChargeType {
  SHIPPING = 'SHIPPING',
  BRAND_CUSTOMIZATION = 'BRAND_CUSTOMIZATION',
  ALTERATION = 'ALTERATION',
}

export interface ICustomCharge {
  chrargeDirection: string;
  amount: number;
  remarks: string;
  type: ChargeType;
}

export interface IOrderPackage {
  id: number;
  version?: number;

  orderId: number;

  customerId?: number;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerGSTIN?: string;
  customerBillingAddressId?: number;
  customerBillingAddress?: IAddress;

  salesChannelOrderId: string;
  salesChannelId: number;
  salesChannelType: SalesChannelType;
  salesFacilityId: number;
  salesFacilityGroupId: number;
  billFromFacilityId?: number;
  salesStaffId: number;

  currentStatus: PackageStatus;
  history: Array<IPackageTrackingDetails>;
  orderLineItemIds: Array<number>;

  fulfillmentFacilityId: number;
  fulfillmentMode: FulfillmentMode;
  fulfillmentFacilityGroupId: number;
  fulfillmentFacilityType?: string;
  brandJitFulfillmentModel?: string;
  unicomFacilityCode?: string;

  customerDeliveryAddress?: IAddress;
  customerDeliveryStoreId?: number;
  customerDeliveryStoreAddress?: IAddress;
  courierTrackingId?: string;
  courierShipmentId?: string;
  courierOrderId?: string;
  courierPartner?: CourierPartner;
  courierPartnerName?: string;
  courierUpdated?: boolean;
  riderId?: number;
  deliveryMode: DeliveryMode;
  storePickupTransferEligible?: boolean;
  isExpress: boolean;
  isPriority: boolean;
  opsTat: number;
  vendorDeliveryDate?: string;
  dispatchByDate?: string;
  warehouseDeliveryByDate?: string;
  vendorToWarehouseTatDays?: number;
  courierTat: number;
  promisedDeliveryDate: string;
  estimatedDeliveryDate: string;
  actualDeliveryDate?: string;

  unicomSaleOrderCode?: string;
  unicomShippingPackageCode?: string;
  invoiceNumber?: string;
  invoiceAsset?: IAsset;
  labelAsset?: IAsset;
  lkGeneratedShippingLabelUrl?: string;

  shippingManifestUrl?: string;

  paymentMethod: PaymentMethod;
  costBreakup: ICostBreakup;

  createdOn: string;
  packedOn?: string;
  invoicedOn?: string;
  cancellationReason: string;
  rtoReason: string;

  courierMetadata: Record<string, string>;
  packingMetadata?: {
    status?: string;
    sessionId?: string;
    source?: string;
    packedOn?: string;
    packedByUserId?: string;
    packedByName?: string;
    orderLineItemIds?: number[];
    items?: Array<{
      orderLineItemId?: number;
      skuCode?: string;
      matchedBy?: string;
      scannedAt?: string;
    }>;
    auditAssets?: Array<{
      asset?: IAsset;
      uploadedOn?: string;
      uploadedByUserId?: string;
      uploadedByName?: string;
      source?: string;
    }>;
    auditUpdatedOn?: string;
    auditUpdatedByUserId?: string;
    auditUpdatedByName?: string;
  };
  riderActionAddress?: Record<string, unknown>;
  deliveryOtpRequired?: boolean;
  deliveryOtpSent?: boolean;
  trackingUrl: string;

  billTo?: {
    name: string;
    address: IAddress;
    phone?: string;
  };
  shipTo?: {
    name: string;
    address: IAddress;
    phone?: string;
  };
  sellerInfo?: {
    logo?: IAsset;
    name?: string;
    address?: IAddress;
    phone?: string;
    email?: string;
    gstin?: string;
    gstLut?: string;
    pan?: string;
    cin?: string;
  };

  recreatePackage?: boolean;
  vendorIds?: Array<number>;
  jitVendorId?: number;
  isTestOrder?: boolean;
  isInternational?: boolean;

  expressServiceabilityBucket?: '60m' | '90m' | '120m' | '150m' | 'beyond';
  crnBelongsToAnotherPackageAsWell?: boolean;
  tryAtHome?: boolean;
  stockTransferId?: number;
  stockTransferRequestNumber?: string;
}

export interface IPackageTrackingDetails {
  status: PackageStatus;
  remark: string;
  eventDate: string; // We'll use string for ZonedDateTime
  icon: string;
  actorId?: number;
}

export enum PaymentGateway {
  CASH = 'CASH',
  CHANGE_RETURN = 'CHANGE_RETURN',
  PINE_LABS = 'PINE_LABS',
  MANUAL = 'MANUAL',
  CREDIT_NOTE = 'CREDIT_NOTE',
  GIFT_VOUCHER = 'GIFT_VOUCHER',
}

export enum PaymentMode {
  CASH = 'CASH',
  UPI = 'UPI',
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD',
  GIFT_VOUCHER = 'GIFT_VOUCHER',
  CREDIT_NOTE = 'CREDIT_NOTE',
}

export enum OrderPaymentStatus {
  INITIATED = 'INITIATED',
  VERIFIED = 'VERIFIED',
  DISCARDED = 'DISCARDED',
  FAILED = 'FAILED',
}

export interface IOrderPayment {
  id: number;
  cartId: string;
  salesChannelOrderId: string;
  salesStaffId: number;
  orderId: number;
  posTerminalId: number;
  paymentGateway: PaymentGateway;
  status: OrderPaymentStatus;
  pgTransactionId: string;
  amount: number;
  metadata?: { [key: string]: string };
  salesChannelId: number;
  paymentTerminalId: number;
  paymentMode: PaymentMode;
  remarks: string;
  cartTotalAmountToBePaid: number;
  cartMinAmountToBePaid: number;
  cartTotalAmountPaid: number;
  createdBy: string;
}

export enum ReturnMode {
  AT_STORE = 'AT_STORE',
  HOME_PICKUP = 'HOME_PICKUP',
}

export enum ReturnStatus {
  CREATED = 'CREATED',
  PICKUP_INITIATED = 'PICKUP_INITIATED',
  OUT_FOR_PICKUP = 'OUT_FOR_PICKUP',
  PICKED_UP = 'PICKED_UP',
  DROPPED_AT_FACILITY = 'DROPPED_AT_FACILITY',
  DELIVERED_TO_WAREHOUSE = 'DELIVERED_TO_WAREHOUSE',
  CANCELLED = 'CANCELLED',
  PICKUP_FAILED = 'PICKUP_FAILED',
}

export enum ReturnCourierExceptionType {
  CANCELLED_RETURN_AWB_MOVED = 'CANCELLED_RETURN_AWB_MOVED',
}

export enum ReturnCourierExceptionResolutionStatus {
  OPEN = 'OPEN',
  MIGRATED = 'MIGRATED',
  IGNORED = 'IGNORED',
  MANUAL_REVIEW = 'MANUAL_REVIEW',
  RESOLVED = 'RESOLVED',
}

export interface IReturnCourierException {
  id: number;
  exceptionType: ReturnCourierExceptionType;
  sourceReturnId: number;
  sourceAwb?: string;
  orderLineItemId: number;
  salesChannelOrderId?: string;
  skuCode?: string;
  sourceReturnStatus?: ReturnStatus;
  sourceReturnCancelledOn?: string;
  latestCourierStatus?: string;
  latestCourierBlobId?: number;
  latestCourierEventOn?: string;
  currentLineStatus?: string;
  currentLineReturnRequestId?: number;
  currentLineRefundRequestId?: number;
  targetReturnId?: number;
  targetReturnStatus?: ReturnStatus;
  resolutionStatus: ReturnCourierExceptionResolutionStatus;
  resolutionReason?: string;
  resolvedBy?: string;
  resolvedOn?: string;
  metadata?: Record<string, unknown>;
}

export interface IReturnCourierEvidenceEvent {
  eventOn?: string;
  concept?: string;
  status?: string;
  activity?: string;
  reason?: string;
  location?: string;
}

export interface IReturnCourierEvidenceAttempt {
  classification: string;
  courierPartner?: CourierPartner;
  courierName?: string;
  awb?: string;
  providerOrderId?: string;
  providerShipmentId?: string;
  latestStatus?: string;
  latestActivity?: string;
  latestReason?: string;
  firstEvidenceOn?: string;
  latestEvidenceOn?: string;
  qcFailed?: boolean;
  qcImageUrls?: string[];
  events?: IReturnCourierEvidenceEvent[];
}

export interface IReturnCourierEvidenceConflict {
  code: string;
  message: string;
  courierPartner?: CourierPartner;
  awb?: string;
  providerOrderId?: string;
  observedOn?: string;
}

export interface IReturnCourierEvidence {
  returnId: number;
  currentIdentity?: {
    courierPartner?: CourierPartner;
    courierName?: string;
    awb?: string;
    providerOrderId?: string;
  };
  setupState: 'NOT_APPLICABLE' | 'PENDING' | 'FAILED' | 'READY';
  currentAttempt?: IReturnCourierEvidenceAttempt;
  previousAttempts?: IReturnCourierEvidenceAttempt[];
  conflicts?: IReturnCourierEvidenceConflict[];
  awaitingWarehouseReceipt?: boolean;
  historyTruncated?: boolean;
  evidenceCount?: number;
}

export interface IReshipRequest {
  id: number;
  createdOn: string;
  returnId: number;
  originalOrderLineItemId: number;
  idempotencyKey: string;
  unicomFacilityCode?: string;
  unicomSaleOrderCode?: string;
  status: OrderLineStatus;
}

export interface IReturnRequest {
  id: number;
  version?: number;
  orderId: number;
  returnMode: ReturnMode;
  customerName: string;
  customerPhone: string;

  pickupAddress?: IAddress;
  refundRequestId: number;
  status: ReturnStatus;
  returnReason: string;
  courierPartner?: CourierPartner;
  courierPartnerName?: string;
  courierTrackingId?: string;
  courierPartnerRequestId?: string;
  latestCourierStatus?: string;
  latestCourierActivity?: string;
  latestCourierReason?: string;
  latestCourierEventOn?: string;
  latestCourierQcFailed?: boolean;
  riderId?: number;
  trackingUrl?: string;
  unicomReversePickupCode?: string;
  returnToFacilityId?: number;
  salesChannelId?: number;
  createdOn: string;
  pickupDate: string;
  jitB2b2cVendorId?: number;

  isExchange: boolean;
  exchangeOrderId: number;
  orderLineItemIds: Array<number>;
  returnItemPutawayDetails: Record<number, ReturnItemPutawayDetails>;
  unicomReturnEnties?: Array<IUnicomReturnEntry>;

  salesChannelOrderId: string;
  history?: Array<IReturnTrackingDetails>;
}

export interface IReturnPickupCoordinationOption {
  targetReturnId: number;
  targetReturnVersion: number;
  status: ReturnStatus;
  courierPartner: CourierPartner;
  courierPartnerName?: string;
  riderId?: number;
  pickupDate?: string;
}

export interface IUnicomReturnEntry {
  itemId: string;
  returnCode?: string;
  putawayCode?: string;
  success: boolean;
}

export interface IReturnTrackingDetails {
  status: ReturnStatus;
  remark: string;
  eventDate: string;
  icon: string;
  actorId?: number;
}

export interface ReturnItemPutawayDetails {
  qcPassed?: boolean;
  shelfCode: string;
  qcFailReason?: string;
  qcFailReasonLabel?: string;
  qcFailedAssets?: Array<IAsset>;
}
